import { z } from 'zod';

import { pageMetaSchema } from '@/lib/api/pagination';
import { emailAddressSchema } from '@/lib/validation/email';
import { usPhoneSchema } from '@/lib/validation/phone';
import { ROLES } from '@/types/roles';

import { CREATABLE_ROLES } from './constants';

/** Active/Inactive/Pending Activation (story 101637; `pending_activation` confirmed 2026-08-04
 *  against `greenway_api`'s `AdminAccountStatus` — a read/filter state a freshly-created user
 *  starts in, not one the enable/disable action can set). */
export const userStatusSchema = z.enum(['active', 'inactive', 'pending_activation']);

/** One Internal Users **table row**. `id` is the record id (used for is-self compare in Phase 3+);
 *  `uniqueId` is the BE's formatted display id `USR-<n>` — the entity column is still an `int`, but
 *  since 2026-09-24 the API renders it through `formatUserId` (it was the bare number before, D6),
 *  so it is displayed verbatim; `phone` is bare 10 digits (D12).
 *
 *  Live-verified 2026-08-04: the LIST response combines the person's name into a single `name`
 *  field (e.g. `"Admin User 4"`) — it does **not** send `firstName`/`lastName` separately, and it
 *  sends no `createdAt` at all (which was never read outside this schema — it's a sort key only,
 *  never rendered). `isLastInRole` hides Delete/Disable later (D5) and is real, but list-only —
 *  see `userDetailSchema` below for the shape returned once you fetch a single user. */
export const userRowSchema = z.object({
  id: z.string(),
  uniqueId: z.string(),
  name: z.string(),
  phone: z.string(),
  email: z.string(),
  role: z.enum(ROLES),
  status: userStatusSchema,
  isLastInRole: z.boolean().optional().default(false),
});

export const userListResponseSchema = z.object({
  data: z.array(userRowSchema),
  meta: pageMetaSchema,
});

/** A single user's **detail** shape (`GET /internal-users/admin/:id`), live-verified 2026-08-04 —
 *  the mirror image of the list row: `firstName`/`lastName` separately, no `name`, no
 *  `isLastInRole`. Used to seed the Edit form, since the list row's combined `name` can't be split
 *  back into first/last reliably. */
export const userDetailSchema = z.object({
  id: z.string(),
  uniqueId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phone: z.string(),
  email: z.string(),
  role: z.enum(ROLES),
  status: userStatusSchema,
});

/** Letters and spaces only (names). Case is preserved; the field caps length at 50 via antd `maxLength`. */
const NAME_RE = /^[A-Za-z ]+$/;

/** One name field: trim, collapse internal runs of whitespace to a single space (ticket 06 — names are
 *  "trimmed/collapsed"), then validate letters+spaces, 1–50 → "Enter a valid name". Shared by first/last. */
const nameFieldSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/\s+/g, ' '))
  .pipe(
    z
      .string()
      .min(1, 'Enter a valid name')
      .max(50, 'Enter a valid name')
      .regex(NAME_RE, 'Enter a valid name'),
  );

/** Create-form validation (story 20–26; D1/D12/D14). Messages are AC-verbatim. Length caps are enforced
 *  live by antd `maxLength` on the inputs — the `.max()` here is the belt-and-braces server-shape guard. */
export const buildUserFormSchema = (storedPhone?: string) =>
  z.object({
    firstName: nameFieldSchema,
    lastName: nameFieldSchema,
    // The shared NANP rule (ADO 118283, ADR-0035). On Edit, `storedPhone` lets an unchanged number
    // that predates the rule through, so a legacy user stays saveable (bug 120891).
    phone: usPhoneSchema('Enter a valid 10-digit US phone number', storedPhone),
    // One message for every malformed-address case — the AC specifies this exact string. The rule
    // itself is shared (`@/lib/validation/email`) so it can only ever be the backend's own
    // `@IsEmail()`, never stricter: ADO 118281 closed both halves of that gap — the RFC 5321 length
    // caps `z.email()` skips, and the fifteen RFC 5322 specials `z.email()` wrongly refuses.
    email: z
      .string()
      .trim()
      .toLowerCase()
      .max(100, 'Enter a valid email address')
      .pipe(emailAddressSchema('Enter a valid email address')),
    role: z.enum(CREATABLE_ROLES, { message: 'Please select a role.' }),
  });

export const userFormSchema = buildUserFormSchema();

/** The form's own field names, derived from the schema so a server `details[].field` can only be
 *  mapped onto an input this form really registers — a `setError` on an unknown path is silently
 *  invisible (ADO 118281). Derived, never re-listed, so it cannot drift from the schema. */
export const USER_FORM_FIELDS = Object.keys(userFormSchema.shape);
